/**
 * Invoice to Client for Expedited Application - Step 16
 * Active only when 1u) Expedited Application is YES.
 * Editable: 3cf) Date of Invoice, Include Invoice No. checkbox.
 */
import { Fragment, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { HiOutlineInformationCircle, HiOutlineX } from "react-icons/hi";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

import invoiceAmountCheck from "../../assets/svg/invoice-amount-check.svg";
import invoiceGenerate from "../../assets/svg/invoice-generate.svg";
import DateTimePicker from "../../components/DateTimePicker";
import ConfirmMessageModal from "components/ConfirmMessageModal";
import { Checkbox } from "components/ui/checkbox";
import { renderWhatsAppFormatting } from "lib/whatsappFormatting";
import {
  getExpeditedInvoiceStatus,
  getExpeditedInvoicePreview,
  saveExpeditedInvoice,
  generateExpeditedInvoice,
  sendExpeditedInvoiceToClient,
  sendExpeditedInvoicePaymentMessage,
  previewSendExpeditedInvoiceToClient,
  previewSendExpeditedInvoicePaymentMessage,
  getLeadDetails,
  type ExpeditedInvoicePreview,
  type ExpeditedInvoiceEmailPreview,
} from "../../api/discussion";
import { getWorkflowStatus } from "api/applications";
import { downloadFileFromUrlWithFallback } from "../../utils/downloadFileFromUrl";
import InactiveStepView from "./InactiveStepView";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const FORM_STYLES = { fontFamily: "Inter, sans-serif" } as const;

const LABEL_CLASS = "text-[12px] font-normal leading-5 text-[#222222]";
const READONLY_CLASS =
  "h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7] px-2.5 py-2 text-[12px] leading-5 text-[#6D6D6D] box-border";
const INPUT_CLASS =
  "h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-white px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222] box-border";

function parseDDMMYYYYToDate(s: string | null | undefined): Date | null {
  if (!s || typeof s !== "string") return null;
  const parts = s.split(/[-/]/).map(Number);
  if (parts.length < 3) return null;
  const [d, m, y] = parts;
  const date = new Date(y, m - 1, d);
  return isNaN(date.getTime()) ? null : date;
}

function toISODateString(date: Date | null): string | undefined {
  if (!date) return undefined;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatDateDisplay(s: string | null | undefined): string {
  if (!s) return "—";
  return s;
}

function formatRupees(n: number | null | undefined): string {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v)) return "Rs. 0/-";
  return `Rs. ${Math.round(v).toLocaleString("en-IN")}/-`;
}

/** Renders a parsed WhatsApp message: bold header line + tight `Label : Value` rows. */
function parsePreviewMessage(message: string): {
  header: string;
  rows: Array<{ label: string; value: string } | { text: string }>;
} {
  const lines = message.split("\n");
  let header = "";
  const rows: Array<{ label: string; value: string } | { text: string }> = [];

  let i = 0;
  for (; i < lines.length; i++) {
    if (lines[i].trim()) {
      header = lines[i].trim();
      i++;
      break;
    }
  }

  while (i < lines.length) {
    const line = lines[i].trim();
    if (!line) {
      i++;
      continue;
    }
    const idx = line.indexOf(":");
    if (idx > 0) {
      const label = line.slice(0, idx).trim();
      const inlineValue = line.slice(idx + 1).trim();
      if (inlineValue) {
        rows.push({ label, value: inlineValue });
        i++;
        continue;
      }
      let j = i + 1;
      while (j < lines.length && !lines[j].trim()) j++;
      if (j < lines.length) {
        rows.push({ label, value: lines[j].trim() });
        i = j + 1;
        continue;
      }
      rows.push({ label, value: "" });
      i++;
      continue;
    }
    rows.push({ text: line });
    i++;
  }

  return { header, rows };
}

function WhatsAppMessageBlock({
  message,
  loading,
}: {
  message: string;
  loading?: boolean;
}) {
  if (loading) {
    return (
      <p className="text-sm text-[#6d6d6d]" style={FORM_STYLES}>
        Loading message preview…
      </p>
    );
  }
  if (!message) {
    return (
      <p className="text-sm text-[#6D6D6D]" style={FORM_STYLES}>
        Message preview not available.
      </p>
    );
  }
  const { header, rows } = parsePreviewMessage(message);
  return (
    <div
      className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-2 gap-y-1.5 font-sans text-[13px] leading-relaxed text-[#222]"
      style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
    >
      {header && (
        <p className="col-span-3 m-0 mb-2 break-words font-medium">
          {renderWhatsAppFormatting(header)}
        </p>
      )}
      {rows.map((row, i) =>
        "label" in row ? (
          <Fragment key={i}>
            <div className="break-words text-[#222]">
              {renderWhatsAppFormatting(row.label)}
            </div>
            <div className="text-[#222]">:</div>
            <div className="min-w-0 break-words text-[#222]">
              {renderWhatsAppFormatting(row.value)}
            </div>
          </Fragment>
        ) : (
          <p key={i} className="col-span-3 m-0 break-words">
            {renderWhatsAppFormatting(row.text)}
          </p>
        )
      )}
    </div>
  );
}

function EmailPreviewBlock({
  preview,
  loading,
  attachmentFallback,
}: {
  preview: ExpeditedInvoiceEmailPreview | null;
  loading?: boolean;
  attachmentFallback?: { filename: string; url: string } | null;
}) {
  if (loading) {
    return (
      <p className="text-sm text-[#6d6d6d]" style={FORM_STYLES}>
        Loading email preview…
      </p>
    );
  }
  if (!preview) {
    return (
      <p className="text-sm text-[#6D6D6D]" style={FORM_STYLES}>
        Email preview not available.
      </p>
    );
  }
  if (!preview.to) {
    return (
      <p className="text-sm text-[#D92D20]" style={FORM_STYLES}>
        No email on file for this client. Email will not be sent.
      </p>
    );
  }
  return (
    <div
      className="flex flex-col gap-1.5 font-sans text-[13px] leading-relaxed text-[#222]"
      style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
    >
      <div className="flex flex-row items-baseline gap-2 break-words">
        <span className="text-[#222]">To</span>
        <span className="text-[#222]">:</span>
        <span className="text-[#222] break-all">{preview.to}</span>
      </div>
      <div className="flex flex-row items-baseline gap-2 break-words">
        <span className="text-[#222]">Subject</span>
        <span className="text-[#222]">:</span>
        <span className="text-[#222] break-words">{preview.subject}</span>
      </div>
      <div className="mt-2 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] p-3">
        <p className="m-0 mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
          Body
        </p>
        <p className="m-0 whitespace-pre-wrap break-words text-[#222]">
          {preview.body}
        </p>
      </div>
      {attachmentFallback?.filename && (
        <div className="mt-2 flex flex-row items-baseline gap-2 break-words">
          <span className="text-[#222]">Attachment</span>
          <span className="text-[#222]">:</span>
          {attachmentFallback.url ? (
            <a
              href={attachmentFallback.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#2E3037] underline underline-offset-2 break-all"
            >
              {attachmentFallback.filename}
            </a>
          ) : (
            <span className="text-[#222] break-all">
              {attachmentFallback.filename}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function PreviewRow({
  label,
  children,
  alignTop = false,
}: {
  label: string;
  children: React.ReactNode;
  alignTop?: boolean;
}) {
  const align = alignTop ? "items-start" : "items-baseline";
  return (
    <>
      <div className={`flex ${align} font-medium text-[#222]`}>{label}</div>
      <div className={`flex ${align} text-[#222]`}>:</div>
      <div className="text-[#222] break-words min-w-0">{children}</div>
    </>
  );
}

function InvoiceDataPreview({
  preview,
  invoiceDate,
}: {
  preview: ExpeditedInvoicePreview;
  invoiceDate: Date | null;
}) {
  const addresseeLines = [
    preview.addresseeLine1,
    preview.addresseeLine2,
    preview.addresseeLine3,
  ]
    .map((l) => (l ?? "").trim())
    .filter(Boolean);

  const liveInvoiceDate = invoiceDate
    ? `${String(invoiceDate.getDate()).padStart(2, "0")}-${String(
        invoiceDate.getMonth() + 1
      ).padStart(2, "0")}-${invoiceDate.getFullYear()}`
    : null;

  return (
    <div
      className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-3 gap-y-1.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2.5 text-[12px] leading-5"
      style={FORM_STYLES}
    >
      <PreviewRow label="Addressee" alignTop>
        {addresseeLines.length > 0 ? (
          <div className="flex flex-col">
            {addresseeLines.map((line, i) => (
              <span key={i}>{line}</span>
            ))}
          </div>
        ) : (
          <span className="text-[#6D6D6D]">Set in Application Details</span>
        )}
      </PreviewRow>
      <PreviewRow label="Form of Address">
        {preview.formOfAddress?.trim() || (
          <span className="text-[#6D6D6D]">Set in Application Details</span>
        )}
      </PreviewRow>
      <PreviewRow label="Mark">
        {preview.mark?.trim() || <span className="text-[#6D6D6D]">—</span>}
      </PreviewRow>
      <PreviewRow label="Expedited Application">
        {preview.expeditedApplication ? "YES" : "NO"}
      </PreviewRow>
      <PreviewRow label="No of Applications">
        {String(preview.noOfApplications ?? 0)}
      </PreviewRow>
      <PreviewRow label="Date of Filing of Expedited Application">
        {formatDateDisplay(preview.dateOfFilingExpedited)}
      </PreviewRow>
      <PreviewRow label="Govt Fee (Expedited)">
        {formatRupees(preview.govtFeeExpedited)}
      </PreviewRow>
      <PreviewRow label="Prof Fee (Expedited)">
        {formatRupees(preview.profFeeExpedited)}
      </PreviewRow>
      <PreviewRow label="Discount in Prof Fee for Expedited">
        {formatRupees(preview.discountExpedited)}
      </PreviewRow>
      <PreviewRow label="Collected Advance Amount">
        {formatRupees(preview.collectedAdvance)}
      </PreviewRow>
      <PreviewRow label="Total Charges">
        {formatRupees(preview.totalCharges)}
      </PreviewRow>
      <PreviewRow label="Invoice No. for Expedited">
        {preview.invoiceNo?.trim() || (
          <span className="text-[#6D6D6D]">Auto-generated on save</span>
        )}
      </PreviewRow>
      <PreviewRow label="Date of Invoice for Expedited">
        {liveInvoiceDate ?? formatDateDisplay(preview.invoiceDate)}
      </PreviewRow>
    </div>
  );
}

type InvoiceToClientForExpeditedApplicationViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
  onSavingChange?: (saving: boolean) => void;
  onSentStatusChange?: (sent: boolean) => void;
};

type PreviewTab = "details" | "pdf" | "message" | "email";

export default function InvoiceToClientForExpeditedApplicationView({
  leadId,
  onSuccess,
  onSavingChange,
  onSentStatusChange,
}: InvoiceToClientForExpeditedApplicationViewProps) {
  const [loading, setLoading] = useState(!!leadId);
  const [preview, setPreview] = useState<ExpeditedInvoicePreview | null>(null);
  const [isActive, setIsActive] = useState(false);

  const [invoiceDate, setInvoiceDate] = useState<Date | null>(null);
  const [includeInvoiceNoInProposal, setIncludeInvoiceNoInProposal] =
    useState(true);
  const [phoneNumber, setPhoneNumber] = useState<string>("");

  const [calcTooltipOpen, setCalcTooltipOpen] = useState(false);
  const calcTooltipRef = useRef<HTMLDivElement>(null);

  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const [pdf, setPdf] = useState<{
    s3Key: string;
    downloadUrl: string;
    filename: string;
  } | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewTab, setPreviewTab] = useState<PreviewTab>("details");
  const [previewNumPages, setPreviewNumPages] = useState<number | null>(null);

  const [hasSent, setHasSent] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMode, setConfirmMode] = useState<"send" | "resend">("send");
  const [whatsappMessage, setWhatsappMessage] = useState<string>("");
  const [paymentMessage, setPaymentMessage] = useState<string>("");
  const [emailPreview, setEmailPreview] =
    useState<ExpeditedInvoiceEmailPreview | null>(null);
  const [previewsLoading, setPreviewsLoading] = useState(false);
  const [previewPdfLoading, setPreviewPdfLoading] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialLoadRef = useRef(true);
  const lastSavedRef = useRef<{
    invoiceDate: string | undefined;
    includeInvoiceNoInProposal: boolean;
  } | null>(null);

  useEffect(() => {
    onSentStatusChange?.(hasSent);
  }, [hasSent, onSentStatusChange]);

  useEffect(() => {
    if (!calcTooltipOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        calcTooltipRef.current &&
        !calcTooltipRef.current.contains(e.target as Node)
      ) {
        setCalcTooltipOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [calcTooltipOpen]);

  const applyPreview = (p: ExpeditedInvoicePreview) => {
    setPreview(p);
    setIsActive(p.isActive);
    setInvoiceDate(parseDDMMYYYYToDate(p.invoiceDate));
    setIncludeInvoiceNoInProposal(p.includeInvoiceNoInProposal ?? true);
    if (p.expeditedInvoicePdfUrl) {
      setPdf((prev) =>
        prev ?? {
          s3Key: "",
          downloadUrl: p.expeditedInvoicePdfUrl as string,
          filename: "expedited-invoice.pdf",
        }
      );
    }
  };

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    initialLoadRef.current = true;

    Promise.all([
      getExpeditedInvoiceStatus(leadId),
      getExpeditedInvoicePreview(leadId),
      getLeadDetails(leadId).catch(() => null),
    ])
      .then(([status, previewRes, leadRes]) => {
        if (cancelled) return;
        setIsActive(status.isActive);
        if (previewRes) {
          applyPreview(previewRes);
        } else if (status.isActive) {
          setInvoiceDate(parseDDMMYYYYToDate(status.invoiceDate));
          setIncludeInvoiceNoInProposal(
            status.includeInvoiceNoInProposal ?? true
          );
        }
        if (leadRes?.lead?.phone) setPhoneNumber(leadRes.lead.phone);
        lastSavedRef.current = {
          invoiceDate: previewRes
            ? toISODateString(parseDDMMYYYYToDate(previewRes.invoiceDate))
            : toISODateString(parseDDMMYYYYToDate(status.invoiceDate)),
          includeInvoiceNoInProposal:
            previewRes?.includeInvoiceNoInProposal ??
            status.includeInvoiceNoInProposal ??
            true,
        };
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load expedited invoice"
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setTimeout(() => {
            initialLoadRef.current = false;
          }, 0);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [leadId]);

  useEffect(() => {
    if (!leadId) {
      setHasSent(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const status = await getWorkflowStatus(leadId);
        const step = status.steps.find(
          (s: { slug: string }) =>
            s.slug === "S2_16C_EXPEDITED_INVOICE_SENT_TO_CLIENT" ||
            s.slug === "expedited-invoice-sent-to-client"
        );
        if (!cancelled && step) setHasSent(step.isCompleted);
      } catch {
        if (!cancelled) setHasSent(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const refreshPreview = async () => {
    if (!leadId) return;
    try {
      const fresh = await getExpeditedInvoicePreview(leadId);
      if (fresh) applyPreview(fresh);
    } catch {
      // non-critical
    }
  };

  // Auto-save (debounced) when preview-relevant fields change.
  useEffect(() => {
    if (!leadId) return;
    if (initialLoadRef.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      const isoDate = toISODateString(invoiceDate);
      const saved = lastSavedRef.current;
      if (
        saved &&
        saved.invoiceDate === isoDate &&
        saved.includeInvoiceNoInProposal === includeInvoiceNoInProposal
      ) {
        return;
      }
      onSavingChange?.(true);
      saveExpeditedInvoice(leadId, {
        invoiceDate: isoDate,
        includeInvoiceNoInProposal,
      })
        .then(async () => {
          lastSavedRef.current = {
            invoiceDate: isoDate,
            includeInvoiceNoInProposal,
          };
          // PDF will need regeneration after a content change
          setPdf(null);
          await refreshPreview();
        })
        .catch((err) => {
          toast.error(
            err instanceof Error ? err.message : "Failed to auto-save invoice"
          );
        })
        .finally(() => {
          onSavingChange?.(false);
        });
    }, 500);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceDate?.getTime(), includeInvoiceNoInProposal, leadId]);

  const persistInvoice = async (): Promise<boolean> => {
    if (!leadId) return false;
    const currentState = {
      invoiceDate: toISODateString(invoiceDate),
      includeInvoiceNoInProposal,
    };
    const saved = lastSavedRef.current;
    if (
      saved &&
      saved.invoiceDate === currentState.invoiceDate &&
      saved.includeInvoiceNoInProposal === currentState.includeInvoiceNoInProposal
    ) {
      return true;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    try {
      await saveExpeditedInvoice(leadId, currentState);
      lastSavedRef.current = currentState;
      setPdf(null);
      await refreshPreview();
      return true;
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save invoice"
      );
      return false;
    }
  };

  const handleGeneratePdf = async () => {
    if (!leadId || generating) return;
    setGenerating(true);
    try {
      const ok = await persistInvoice();
      if (!ok) return;
      const result = await generateExpeditedInvoice(leadId);
      setPdf(result);
      await refreshPreview();
      toast.success("Expedited invoice PDF generated.");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to generate PDF"
      );
    } finally {
      setGenerating(false);
    }
  };

  const ensurePdf = async (): Promise<{
    s3Key: string;
    downloadUrl: string;
    filename: string;
  } | null> => {
    if (pdf?.downloadUrl) return pdf;
    if (!leadId) return null;
    const ok = await persistInvoice();
    if (!ok) return null;
    try {
      const result = await generateExpeditedInvoice(leadId);
      setPdf(result);
      return result;
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to generate PDF"
      );
      return null;
    }
  };

  const handleDownload = async () => {
    if (!leadId || downloading) return;
    setDownloading(true);
    try {
      const generated = await ensurePdf();
      if (!generated) return;
      const result = await downloadFileFromUrlWithFallback(
        generated.downloadUrl,
        generated.filename ?? "expedited-invoice.pdf"
      );
      if (result === "local") {
        toast.success("PDF saved to your device.");
      } else {
        toast.info(
          "Opened the PDF in a new tab. Use Save or Save as to store it locally."
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const refreshSendPreviews = async () => {
    if (!leadId) return;
    setPreviewsLoading(true);
    try {
      const [sendPreview, paymentPreview] = await Promise.all([
        previewSendExpeditedInvoiceToClient(leadId, {
          sendWhatsApp: true,
          sendEmail: true,
        }).catch(() => null),
        previewSendExpeditedInvoicePaymentMessage(leadId).catch(() => null),
      ]);
      if (sendPreview) {
        setWhatsappMessage(sendPreview.whatsappMessage ?? "");
        setEmailPreview(sendPreview.emailPreview ?? null);
      }
      if (paymentPreview) {
        setPaymentMessage(paymentPreview.paymentMessage ?? "");
      }
    } finally {
      setPreviewsLoading(false);
    }
  };

  const handlePreview = () => {
    if (!leadId) return;
    setPreviewTab("details");
    setPreviewNumPages(null);
    setPreviewModalOpen(true);
    if (!pdf?.downloadUrl) {
      setPreviewPdfLoading(true);
    }
    void (async () => {
      try {
        await ensurePdf();
        await refreshSendPreviews();
      } finally {
        setPreviewPdfLoading(false);
      }
    })();
  };

  const closePreviewModal = () => {
    setPreviewModalOpen(false);
    setPreviewNumPages(null);
    setPreviewTab("details");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;

    if (hasSent) {
      setSaving(true);
      onSavingChange?.(true);
      try {
        const ok = await persistInvoice();
        if (ok) {
          toast.success("Invoice saved successfully.");
          onSuccess?.(leadId);
        }
      } finally {
        setSaving(false);
        onSavingChange?.(false);
      }
      return;
    }

    setSaving(true);
    onSavingChange?.(true);
    try {
      const ok = await persistInvoice();
      if (!ok) return;
      await ensurePdf();
      await refreshSendPreviews();
      setConfirmMode("send");
      setShowConfirmModal(true);
    } finally {
      setSaving(false);
      onSavingChange?.(false);
    }
  };

  const handleConfirmProceed = async () => {
    if (!leadId) return;
    setShowConfirmModal(false);
    setSaving(true);
    onSavingChange?.(true);
    try {
      const [invoiceResult, paymentResult] = await Promise.allSettled([
        sendExpeditedInvoiceToClient(leadId, {
          sendWhatsApp: true,
          sendEmail: true,
        }),
        sendExpeditedInvoicePaymentMessage(leadId),
      ]);
      const invoiceOk = invoiceResult.status === "fulfilled";
      const paymentOk = paymentResult.status === "fulfilled";
      const invoiceErr =
        invoiceResult.status === "rejected"
          ? invoiceResult.reason instanceof Error
            ? invoiceResult.reason.message
            : "Failed to send invoice to client"
          : null;
      const paymentErr =
        paymentResult.status === "rejected"
          ? paymentResult.reason instanceof Error
            ? paymentResult.reason.message
            : "Failed to send payment message"
          : null;
      const isResend = confirmMode === "resend";

      if (invoiceOk && paymentOk) {
        setHasSent(true);
        toast.success(
          isResend
            ? "Expedited invoice resent to client successfully."
            : "Expedited invoice sent to client successfully."
        );
        await refreshPreview();
        onSuccess?.(leadId);
      } else if (invoiceOk && !paymentOk) {
        setHasSent(true);
        toast.warning(
          `Invoice sent to client, but payment message failed: ${paymentErr}`
        );
        await refreshPreview();
        onSuccess?.(leadId);
      } else if (!invoiceOk && paymentOk) {
        toast.warning(
          `Payment message sent, but sending invoice failed: ${invoiceErr}`
        );
      } else {
        toast.error(invoiceErr ?? "Failed to send invoice to client");
      }
    } finally {
      setSaving(false);
      onSavingChange?.(false);
    }
  };

  const handleResend = async () => {
    if (!leadId || saving) return;
    setSaving(true);
    onSavingChange?.(true);
    try {
      const ok = await persistInvoice();
      if (!ok) return;
      await ensurePdf();
      await refreshSendPreviews();
      setConfirmMode("resend");
      setShowConfirmModal(true);
    } finally {
      setSaving(false);
      onSavingChange?.(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex w-full max-w-[1085px] flex-col gap-5"
        style={FORM_STYLES}
      >
        <p className="text-xs text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this form.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex w-full max-w-[1085px] items-center justify-center py-12"
        style={FORM_STYLES}
      >
        <p className="text-sm text-[#6D6D6D]">Loading expedited invoice...</p>
      </div>
    );
  }

  if (!isActive) {
    return (
      <InactiveStepView
        title="Not Required"
        body='Expedited Application was marked as "NO". Step 16 is inactive.'
        buttonText="Next Step"
        onButtonClick={() => onSuccess?.(leadId ?? "")}
      />
    );
  }

  const displayTotalCharges = formatRupees(preview?.totalCharges ?? 0);
  const hasBreakdown =
    preview &&
    (preview.govtFeeExpedited != null ||
      preview.profFeeExpedited != null ||
      preview.discountExpedited != null ||
      preview.collectedAdvance != null);

  return (
    <form
      id="expedited-invoice-form"
      onSubmit={handleSubmit}
      className="flex w-full max-w-[1085px] flex-col gap-5"
      style={FORM_STYLES}
    >
      {/* Row 1 (editable + computed): Date | Invoice Amount | Discount */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>
            Date of Invoice for Expedited (3cf)
          </label>
          <DateTimePicker
            value={invoiceDate}
            onChange={setInvoiceDate}
            placeholder="Select date"
            showTime={false}
            buttonClassName="!h-9 !min-h-0 !text-[12px] rounded-[6px]"
          />
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1">
            <label className={LABEL_CLASS}>Invoice Amount for Expedited</label>
            {hasBreakdown && (
              <div className="relative" ref={calcTooltipRef}>
                <button
                  type="button"
                  onClick={() => setCalcTooltipOpen((o) => !o)}
                  className="flex items-center justify-center text-[#6D6D6D] hover:text-[#222222]"
                  aria-label="Show calculation"
                >
                  <HiOutlineInformationCircle
                    className="w-4 h-4"
                    strokeWidth={1.5}
                  />
                </button>
                {calcTooltipOpen && preview && (
                  <div
                    className="absolute left-0 top-full mt-1 z-30 w-72 rounded-[8px] border border-[#EEEEEE] bg-white shadow-lg p-3 text-[12px] leading-5 text-[#222222]"
                    style={FORM_STYLES}
                  >
                    <p className="text-xs font-semibold text-[#101111] mb-2">
                      Calculation Breakdown
                    </p>
                    <div className="flex flex-col gap-1">
                      <div className="flex justify-between gap-3">
                        <span className="text-[#6D6D6D]">
                          Govt Fee (Expedited)
                        </span>
                        <span className="shrink-0">
                          {formatRupees(preview.govtFeeExpedited)}
                        </span>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span className="text-[#6D6D6D]">
                          Prof Fee (Expedited)
                        </span>
                        <span className="shrink-0">
                          {formatRupees(preview.profFeeExpedited)}
                        </span>
                      </div>
                      {preview.discountExpedited > 0 && (
                        <div className="flex justify-between gap-3 text-red-600">
                          <span>− Discount (Expedited)</span>
                          <span className="shrink-0">
                            {formatRupees(preview.discountExpedited)}
                          </span>
                        </div>
                      )}
                      {preview.collectedAdvance > 0 && (
                        <div className="flex justify-between gap-3 text-red-600">
                          <span>− Collected Advance</span>
                          <span className="shrink-0">
                            {formatRupees(preview.collectedAdvance)}
                          </span>
                        </div>
                      )}
                      <div className="border-t border-[#222] pt-1 mt-1 flex justify-between font-semibold text-[#101111]">
                        <span>Invoice Amount</span>
                        <span>{formatRupees(preview.totalCharges)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <input
            type="text"
            value={displayTotalCharges}
            readOnly
            className={READONLY_CLASS}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Discount in Prof Fee for Expedited</label>
          <input
            type="text"
            value={formatRupees(preview?.discountExpedited ?? 0)}
            readOnly
            className={READONLY_CLASS}
          />
        </div>
      </div>

      {/* Row 2 (read-only context): Invoice No. | Mark | Govt Fee */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Invoice No. for Expedited (3ce)</label>
          <input
            type="text"
            value={preview?.invoiceNo ?? ""}
            placeholder="Auto-generated on save"
            readOnly
            className={READONLY_CLASS}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Mark</label>
          <input
            type="text"
            value={preview?.mark ?? ""}
            readOnly
            className={READONLY_CLASS}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Govt Fee (Expedited)</label>
          <input
            type="text"
            value={formatRupees(preview?.govtFeeExpedited ?? 0)}
            readOnly
            className={READONLY_CLASS}
          />
        </div>
      </div>

      {/* Row 3 (read-only context): Collected Advance | No of Applications | Prof Fee */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Collected Advance Amount</label>
          <input
            type="text"
            value={formatRupees(preview?.collectedAdvance ?? 0)}
            readOnly
            className={READONLY_CLASS}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>No of Applications</label>
          <input
            type="text"
            value={
              preview?.noOfApplications != null
                ? String(preview.noOfApplications)
                : ""
            }
            readOnly
            className={READONLY_CLASS}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Prof Fee (Expedited)</label>
          <input
            type="text"
            value={formatRupees(preview?.profFeeExpedited ?? 0)}
            readOnly
            className={READONLY_CLASS}
          />
        </div>
      </div>

      {/* Row 4 (read-only context): Date of Filing of Expedited Application */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>
            Date of Filing of Expedited Application
          </label>
          <input
            type="text"
            value={preview?.dateOfFilingExpedited ?? ""}
            readOnly
            className={READONLY_CLASS}
          />
        </div>
      </div>

      {/* Row 5 (read-only context, per source doc pg 57): Addressee | Form of Address | Expedited Application */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Addressee</label>
          <input
            type="text"
            value={
              [
                preview?.addresseeLine1,
                preview?.addresseeLine2,
                preview?.addresseeLine3,
              ]
                .map((l) => l?.trim())
                .filter(Boolean)
                .join(", ") || "Set in Application Details"
            }
            readOnly
            className={READONLY_CLASS}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Form of Address</label>
          <input
            type="text"
            value={preview?.formOfAddress?.trim() || "Set in Application Details"}
            readOnly
            className={READONLY_CLASS}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Expedited Application</label>
          <input
            type="text"
            value={preview?.expeditedApplication ? "YES" : "NO"}
            readOnly
            className={READONLY_CLASS}
          />
        </div>
      </div>

      {/* Invoice Amount summary bar */}
      <div
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-3 rounded-[8px] px-4 sm:px-5 py-4 sm:py-0 min-h-[72px] sm:h-[72px]"
        style={{ background: "#222222" }}
      >
        <div className="flex flex-row items-center gap-2 min-w-0">
          <img
            src={invoiceAmountCheck}
            alt=""
            className="w-5 h-5 flex-shrink-0"
          />
          <span
            className="text-[12px] md:text-sm font-normal leading-5 text-white/90 truncate"
            style={FORM_STYLES}
          >
            INVOICE AMOUNT FOR EXPEDITED
          </span>
        </div>
        <span
          className="text-lg md:text-[22px] font-bold leading-8 md:leading-10 text-white shrink-0"
          style={FORM_STYLES}
        >
          {displayTotalCharges}
        </span>
      </div>

      {/* Include Invoice No checkbox */}
      <div className="flex items-center gap-2">
        <Checkbox
          id="include-invoice-no-expedited"
          checked={includeInvoiceNoInProposal}
          onCheckedChange={(checked) =>
            setIncludeInvoiceNoInProposal(Boolean(checked))
          }
        />
        <label
          htmlFor="include-invoice-no-expedited"
          className="text-xs md:text-sm text-[#222222] cursor-pointer"
          style={FORM_STYLES}
        >
          Include Invoice No. in PDF
        </label>
      </div>

      {/* Generate PDF + Download + Preview */}
      <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2.5">
        <div className="flex flex-row items-center gap-2.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] pl-4 pr-2 py-2 min-h-[44px] sm:h-11">
          <img
            src={invoiceGenerate}
            alt=""
            className="w-[22px] h-[22px] flex-shrink-0"
          />
          <button
            type="button"
            onClick={handleGeneratePdf}
            disabled={generating}
            className="flex flex-row items-center justify-center min-h-[40px] h-9 px-4 rounded-[6px] bg-[#222222] text-[12px] md:text-xs font-medium text-white hover:bg-[#333333] transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
            style={FORM_STYLES}
          >
            {generating
              ? "Generating..."
              : pdf
                ? "Regenerate PDF"
                : "Generate PDF"}
          </button>
        </div>
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading || generating}
          className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] md:text-xs font-medium text-[#222222] hover:bg-[#F7F7F7] transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
          style={FORM_STYLES}
        >
          {downloading ? "Downloading…" : "Download"}
        </button>
        <button
          type="button"
          onClick={handlePreview}
          disabled={generating}
          className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] md:text-xs font-medium text-[#222222] hover:bg-[#F7F7F7] transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
          style={FORM_STYLES}
        >
          Preview
        </button>
      </div>

      {/* Phone number + optional Resend */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="tel"
          inputMode="numeric"
          value={phoneNumber}
          readOnly
          placeholder="e.g. +919876543210"
          className="w-full min-w-0 sm:w-[200px] h-9 px-[10px] py-2 text-[12px] text-[#222] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] placeholder:text-[#6D6D6D]"
        />
        {hasSent && (
          <button
            type="button"
            disabled={saving}
            onClick={handleResend}
            className="inline-flex items-center justify-center h-9 px-4 text-[12px] font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? "Saving…" : "Resend Message"}
          </button>
        )}
      </div>

      <ConfirmMessageModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title={
          confirmMode === "resend"
            ? "Resend expedited invoice to client"
            : "Confirm expedited invoice to client"
        }
        previewSlot={
          <div className="flex flex-col gap-4 p-4">
            <section>
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                WhatsApp message (with invoice PDF link)
              </p>
              <WhatsAppMessageBlock
                message={whatsappMessage}
                loading={previewsLoading}
              />
            </section>
            <div className="border-t border-dashed border-[#EEEEEE]" />
            <section>
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Payment details (WhatsApp follow-up after invoice)
              </p>
              <WhatsAppMessageBlock
                message={paymentMessage}
                loading={previewsLoading}
              />
            </section>
            <div className="border-t border-dashed border-[#EEEEEE]" />
            <section>
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Email (with invoice PDF)
              </p>
              <EmailPreviewBlock
                preview={emailPreview}
                loading={previewsLoading}
                attachmentFallback={
                  pdf ? { filename: pdf.filename, url: pdf.downloadUrl } : null
                }
              />
            </section>
          </div>
        }
        onConfirm={() => {
          void handleConfirmProceed();
        }}
        disableConfirm={saving}
      />

      {previewModalOpen && preview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4"
          role="dialog"
          aria-modal="true"
          onClick={closePreviewModal}
        >
          <div
            className="relative flex h-[95vh] sm:h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-center justify-between border-b border-[#e3e3e3] px-3 py-3 sm:px-4">
              <span
                className="truncate pr-2 text-xs font-medium text-[#222] sm:text-sm"
                style={FORM_STYLES}
              >
                {pdf?.filename ?? "Expedited invoice preview"}
              </span>
              <div className="flex items-center gap-3 shrink-0">
                {pdf?.downloadUrl && (
                  <a
                    href={pdf.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[#2E3037] underline underline-offset-2"
                  >
                    Open in new tab
                  </a>
                )}
                <button
                  type="button"
                  onClick={closePreviewModal}
                  className="flex size-10 shrink-0 items-center justify-center rounded text-[#6d6d6d] hover:bg-[#eee] hover:text-[#222] sm:size-8 touch-manipulation"
                  aria-label="Close preview"
                >
                  <HiOutlineX className="size-5" strokeWidth={2} />
                </button>
              </div>
            </div>

            <div
              className="flex shrink-0 gap-1 border-b border-[#e3e3e3] bg-white px-2 sm:px-3"
              role="tablist"
            >
              <button
                type="button"
                role="tab"
                aria-selected={previewTab === "details"}
                onClick={() => setPreviewTab("details")}
                className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
                  previewTab === "details"
                    ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
                    : "text-[#6d6d6d] hover:text-[#222]"
                }`}
              >
                Details
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={previewTab === "pdf"}
                onClick={() => setPreviewTab("pdf")}
                disabled={!pdf?.downloadUrl && !previewPdfLoading}
                className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation disabled:cursor-not-allowed disabled:opacity-50 ${
                  previewTab === "pdf"
                    ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
                    : "text-[#6d6d6d] hover:text-[#222]"
                }`}
              >
                {previewPdfLoading && !pdf?.downloadUrl
                  ? "Generated PDF (loading…)"
                  : "Generated PDF"}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={previewTab === "message"}
                onClick={() => setPreviewTab("message")}
                className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
                  previewTab === "message"
                    ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
                    : "text-[#6d6d6d] hover:text-[#222]"
                }`}
              >
                WhatsApp Message
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={previewTab === "email"}
                onClick={() => setPreviewTab("email")}
                className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
                  previewTab === "email"
                    ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
                    : "text-[#6d6d6d] hover:text-[#222]"
                }`}
              >
                Email
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-auto bg-[#fafafa] p-2 sm:p-4 flex justify-center">
              {previewTab === "pdf" ? (
                pdf?.downloadUrl ? (
                  <Document
                    file={pdf.downloadUrl}
                    onLoadSuccess={({ numPages: n }) => setPreviewNumPages(n)}
                    loading={
                      <p className="text-sm text-[#6d6d6d]">Loading PDF…</p>
                    }
                    error={
                      <p className="text-sm text-red-600">Failed to load PDF.</p>
                    }
                    className="flex flex-col items-center gap-4"
                  >
                    {previewNumPages != null &&
                      Array.from({ length: previewNumPages }, (_, i) => (
                        <Page
                          key={i + 1}
                          pageNumber={i + 1}
                          width={Math.min(
                            800,
                            typeof window !== "undefined"
                              ? window.innerWidth - 48
                              : 800
                          )}
                          renderTextLayer
                          renderAnnotationLayer
                        />
                      ))}
                  </Document>
                ) : previewPdfLoading ? (
                  <p className="text-sm text-[#6d6d6d]">Generating PDF…</p>
                ) : (
                  <p className="text-sm text-[#6D6D6D]">No PDF available.</p>
                )
              ) : previewTab === "details" ? (
                <div
                  className="flex w-full max-w-3xl flex-col gap-3"
                  style={FORM_STYLES}
                >
                  <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                      Invoice details
                    </p>
                    <InvoiceDataPreview
                      preview={preview}
                      invoiceDate={invoiceDate}
                    />
                  </section>
                </div>
              ) : previewTab === "message" ? (
                <div
                  className="w-full max-w-2xl rounded-lg border border-[#EEEEEE] bg-white p-4 shadow-sm sm:p-5"
                  style={FORM_STYLES}
                >
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                    Will be sent to client WhatsApp with the invoice PDF link
                  </p>
                  <WhatsAppMessageBlock
                    message={whatsappMessage}
                    loading={previewsLoading}
                  />
                  {paymentMessage && (
                    <>
                      <div className="my-4 border-t border-dashed border-[#EEEEEE]" />
                      <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                        Followed by payment details message
                      </p>
                      <WhatsAppMessageBlock
                        message={paymentMessage}
                        loading={previewsLoading}
                      />
                    </>
                  )}
                </div>
              ) : (
                <div
                  className="w-full max-w-2xl rounded-lg border border-[#EEEEEE] bg-white p-4 shadow-sm sm:p-5"
                  style={FORM_STYLES}
                >
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                    Will be emailed to the client with the invoice PDF
                  </p>
                  <EmailPreviewBlock
                    preview={emailPreview}
                    loading={previewsLoading}
                    attachmentFallback={
                      pdf ? { filename: pdf.filename, url: pdf.downloadUrl } : null
                    }
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </form>
  );
}
